/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Upload With Options - Image upload OR Text input toggle
 * Updated: 2026-01-09
 */

import React, { useState, useRef } from 'react';
import { Upload, X, FileText } from 'lucide-react';
import { modalApi } from '../../services/modalApi';
import { indexedDBService } from '../../services/indexedDbService';
import { compressAndResizeImage } from '../../lib/imageUtils';
import { showToast } from '../../lib/toast';
import { calculateSmartThresholdWithHistogram } from '../../services/imageAnalysisService';
import TextLogoBuilder from './TextLogoBuilder';
import ImageProcessingOverlay from '../ImageProcessingOverlay';

interface UploadWithOptionsProps {
  onImageSelected: (imageData: {
    imageBase64: string;
    imageId: string;
    imagePath: string;
    smartThreshold?: number;
  }) => void;
  onClear?: () => void;
  onCustomPromptChange?: (prompt: string) => void;
  onModeChange?: (mode: 'image' | 'text') => void;
  disabled?: boolean;
  mockupImage?: string;
  customPrompt?: string;
  previewComponent?: React.ReactNode;
}

type UploadMode = 'image' | 'text';

const UploadWithOptions: React.FC<UploadWithOptionsProps> = ({
  onImageSelected,
  onClear,
  onCustomPromptChange,
  onModeChange,
  disabled = false,
  mockupImage,
  customPrompt = '',
  previewComponent,
}) => {
  const [mode, setMode] = useState<UploadMode>('image');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadMessage, setUploadMessage] = useState('');
  const [previewImage, setPreviewImage] = useState<string>('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageSelect = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      showToast.error('Vui lòng chọn tệp hình ảnh');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadMessage('Đang chuẩn bị...');

    try {
      // Show preview locally
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);

      // Compress
      setUploadProgress(25);
      setUploadMessage('Đang tối ưu hóa ảnh...');
      const compressed = await compressAndResizeImage(file);
      setUploadProgress(50);

      // Upload to Modal
      setUploadProgress(75);
      setUploadMessage('Sắp xong rồi, bạn đợi một tí nhé !');
      const uploadResp = await modalApi.uploadImage(
        compressed,
        undefined,
        undefined,
        'original'
      );

      if (!uploadResp.success) {
        throw new Error(`Upload failed: ${uploadResp.message}`);
      }

      setUploadProgress(100);
      setUploadMessage('Hoàn tất!');

      // Cache to IndexedDB
      try {
        const imageId = crypto.randomUUID();
        await indexedDBService.saveDesignImage(imageId, compressed);
        console.log('✅ Image cached to IndexedDB');
      } catch (cacheErr) {
        console.warn('⚠️ Failed to cache image:', cacheErr);
      }

      // Calculate smart threshold using histogram analysis
      let smartThreshold = 50; // fallback default (median-based, more aggressive detail)
      try {
        setUploadMessage('Đang phân tích hình ảnh...');
        smartThreshold = await calculateSmartThresholdWithHistogram(compressed);
      } catch (thresholdErr) {
        console.warn('⚠️ Failed to calculate smart threshold, using default 50:', thresholdErr);
      }

      onImageSelected({
        imageBase64: compressed,
        imageId: uploadResp.image_id,
        imagePath: uploadResp.filepath,
        smartThreshold,
      });

    } catch (err) {
      console.error('❌ Upload failed:', err);
      const errorMsg = err instanceof Error ? err.message : 'Upload failed';
      showToast.error(`Tải lên thất bại: ${errorMsg}`);
      setPreviewImage('');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleImageSelect(file);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageSelect(file);
    }
  };

  const handleClear = () => {
    setPreviewImage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    // Call parent callback to reset design options
    if (onClear) {
      onClear();
    }
  };


  const displayImage = mockupImage || previewImage;

  return (
    <div className={`w-full h-full flex flex-col relative ${mode === 'text' ? 'overflow-visible' : 'overflow-hidden'}`}>
        {/* Global Loading Overlay for Upload */}
        {uploading && (
          <div className="fixed inset-0 z-[100] pointer-events-none flex items-center justify-center">
            <div className="pointer-events-auto">
              <ImageProcessingOverlay
                isLoading={true}
                message={uploadMessage}
                progress={uploadProgress}
              />
            </div>
          </div>
        )}

        {/* Mode Toggle - Large clickable buttons */}
        <div className="flex gap-2 mb-2 lg:mb-4 flex-shrink-0 relative z-20">
          <button
            onClick={() => {
              setMode('image');
              onModeChange?.('image');
            }}
            disabled={disabled}
            className={`flex-1 py-3 lg:py-3.5 px-3 lg:px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 active:scale-95 ${
              mode === 'image'
                ? 'bg-[rgb(6,182,212)] text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            } disabled:opacity-50 disabled:cursor-not-allowed text-sm lg:text-base`}
            title="Tạo logo từ hình ảnh"
          >
            <Upload className="w-4 h-4 lg:w-5 lg:h-5 flex-shrink-0" />
            <span>Từ Hình</span>
          </button>
          <button
            onClick={() => {
              setMode('text');
              onModeChange?.('text');
            }}
            disabled={disabled}
            className={`flex-1 py-3 lg:py-3.5 px-3 lg:px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 active:scale-95 ${
              mode === 'text'
                ? 'bg-[rgb(6,182,212)] text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            } disabled:opacity-50 disabled:cursor-not-allowed text-sm lg:text-base`}
            title="Tạo logo từ chữ cái"
          >
            <FileText className="w-4 h-4 lg:w-5 lg:h-5 flex-shrink-0" />
            <span>Từ Text</span>
          </button>
        </div>

        {/* Image Upload Mode */}
        {mode === 'image' && (
          <div className="flex-1 flex flex-col overflow-hidden">
            {previewComponent ? (
              <div className="relative bg-gray-50 rounded-lg border border-gray-200 overflow-hidden flex-1 flex flex-col min-h-0 items-center justify-center">
                 <div className="flex-1 w-full h-full relative min-h-0">
                    {previewComponent}
                 </div>
              </div>
            ) : displayImage ? (
              <div className="relative bg-gray-50 rounded-lg border border-gray-200 p-4 lg:p-6 flex flex-col items-center justify-center flex-1 min-h-0">
                <img
                  src={displayImage}
                  alt="Preview"
                  className="max-h-full max-w-full object-contain rounded-lg shadow-lg"
                />

                <button
                  onClick={handleClear}
                  className="absolute top-2 right-2 lg:top-3 lg:right-3 p-1.5 lg:p-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition-colors"
                  title="Xóa ảnh để chọn hình khác hoặc kéo hình mới vào"
                >
                  <X className="w-4 h-4 lg:w-5 lg:h-5" />
                </button>
              </div>
            ) : (
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`relative border-2 border-dashed rounded-lg p-6 lg:p-12 text-center cursor-pointer transition-all flex-1 flex flex-col items-center justify-center min-h-0 overflow-hidden ${
                  disabled
                    ? 'bg-gray-50 border-gray-200 cursor-not-allowed opacity-50'
                    : dragActive
                    ? 'border-[rgb(6,182,212)] shadow-lg'
                    : 'border-[rgb(6,182,212)] hover:bg-cyan-50'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleInputChange}
                  disabled={disabled || uploading}
                  className="hidden"
                />

                <div className="space-y-2 lg:space-y-3">
                  <Upload className="w-8 h-8 lg:w-12 lg:h-12 mx-auto" style={{ color: 'rgb(6, 182, 212)' }} />
                  <div>
                    <p className="text-gray-900 font-semibold text-base lg:text-lg">
                      Để khắc laser đẹp nhất
                    </p>
                    <p className="text-[10px] lg:text-sm text-gray-600 mt-2 leading-relaxed">Hãy gõ trực tiếp nội dung, hoặc tải logo/hình đơn sắc đơn giản (PNG/JPG, nền trong suốt hoặc nền trắng). Vui lòng cắt bỏ phần trống dư để hình hiển thị rõ nét hơn.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Warning message - Hidden */}
            {/* <p className="text-[10px] lg:text-xs text-gray-500 mt-2 lg:mt-4 flex-shrink-0">
              ⚠️ Ảnh sẽ được upload ngay. Đảm bảo kết nối Internet ổn định.
            </p> */}
          </div>
        )}

        {/* Text Logo Builder Mode - Full screen on mobile, scrollable on desktop */}
        {mode === 'text' && (
          <div className="flex-1 min-h-0 overflow-visible relative">
            <div className="p-2 lg:p-4 h-full w-full flex flex-col">
              <TextLogoBuilder
                  onLogoGenerated={(data) => {
                      setPreviewImage(data.imageBase64);
                      onImageSelected(data);
                      setMode('image');
                      onModeChange?.('image');
                  }}
                  disabled={disabled}
              />
            </div>
          </div>
        )}
    </div>
  );
};

export default UploadWithOptions;
